<template>
    <div class="image-gallery" ref="imageGallery" :class="{ 'image-gallery--opened': showImage }">
        <div v-if="showImage" class="image-gallery__overlay"></div>

        <Dismissible :show.sync="showImage" except-selector=".image-gallery__swiper">
            <div v-if="activeImage" class="image-gallery__active" ref="activeImage">
                <ImageFluid
                    :retina-src="activeImage.xlarge"
                    :src="activeImage.large"
                    :alt="activeImage.altText"
                    class="image-gallery__active-image"
                    width="750"
                    height="550"
                    @load="activeImageLoaded"
                />

                <div v-show="activeImageLoading" class="image-gallery__active-loading">
                    <IconSvg filepath="spinner.svg" class="image-gallery__active-loading-icon" />
                </div>

                <button class="image-gallery__active-close btn" @click="closeImage" :title="$t('Generic.close')">
                    <IconSvg filepath="close.svg" />
                </button>
            </div>
        </Dismissible>

        <Swiper
            :options="swiperOptions"
            class="image-gallery__swiper image-gallery__swiper--intact"
            @load="onSwiperLoad"
            @slideChange="setActiveImage"
            @tap="onSwiperClick"
            @click.native.once="activateSwiper"
            ref="thumbnails"
        >
            <div
                v-for="(image, imageIndex) in images"
                :key="'image' + imageIndex"
                class="image-gallery__thumbnail swiper-slide image-gallery__thumbnail--hidden"
            >
                <img
                    :src="image.small"
                    :alt="image.altText"
                    width="150"
                    height="110"
                    loading="lazy"
                    class="image-gallery__thumbnail-image img-fluid"
                />
            </div>

            <template #after>
                <button class="image-gallery__prev-button btn">
                    <IconSvg filepath="gallery/chevron.svg" class="image-gallery__prev-icon" />
                </button>
                <button class="image-gallery__next-button btn">
                    <IconSvg filepath="gallery/chevron.svg" class="image-gallery__next-icon" />
                </button>
            </template>
        </Swiper>
    </div>
</template>

<script>
import ImageLazy from '@/components/ImageLazy';
import Swiper from '@/components/swiper/Swiper';
import { createPopper } from '@popperjs/core';
import Dismissible from '@/components/Dismissible';
import ImageFluid from '@/components/ImageFluid';
import Modal from '../Modal';

export default {
    name: 'ImageGalleryFloating',
    data() {
        const activeClassName = 'image-gallery__thumbnail--active';
        return {
            activeImage: null,
            swiperInstance: null,
            activeClassName,
            activeImageLoading: false,
            popperInstance: null,

            swiperOptions: {
                slidesPerView: 'auto',
                slideToClickedSlide: true,
                slideActiveClass: activeClassName,
                allowTouchMove: true,
                simulateTouch: true,
                centeredSlides: true,
                loop: true,
                loopedSlides: this.images.length * 2,
                navigation: {
                    prevEl: '.image-gallery__prev-button',
                    nextEl: '.image-gallery__next-button'
                },
                keyboard: true,
                on: {
                    keyPress: this.closeOnEscape
                },
                followFinger: false,
                preloadImages: false
            }
        };
    },

    components: {
        Modal,
        ImageFluid,
        ImageLazy,
        Swiper,
        Dismissible
    },

    props: {
        images: {
            type: Array,
            required: true
        }
    },

    computed: {
        showImage: {
            get() {
                return !!this.activeImage;
            },
            set(value) {
                if (!value) {
                    this.activeImage = null;
                }
            }
        }
    },

    methods: {
        activateSwiper() {
            // manually deactivating first thumbnail, css does this better
            this.$refs.thumbnails.$el.classList.remove('image-gallery__swiper--intact');
        },
        onSwiperLoad(swiper) {
            this.swiperInstance = swiper;
            this.fixFirstSlideChange();
        },
        setActiveImage(swiper) {
            const index = swiper.originalParams.loop ? swiper.realIndex : swiper.activeIndex;
            this.activeImage = this.images[index];
        },
        onSwiperClick(swiper) {
            this.setActiveImage(swiper);

            //fixes blank thumbnails after clicking at the wrapper edge
            swiper.once('slideChangeTransitionEnd', () => {
                swiper.loopFix();
            });
        },
        fixFirstSlideChange() {
            //swiper doesn't activate thumbnail on first slide change with loop mode
            // transitionEnd for case when navigation via buttons
            const handler = () => {
                setTimeout(() => {
                    this.swiperInstance.update();
                }, 100);
            };

            this.swiperInstance.once('slideChangeTransitionEnd', handler);
            // tap / click for case when interacting with thumbnail
            this.swiperInstance.once('click', handler);
            //this.swiperInstance.once('tap', handler);
        },
        deactivateThumbnail() {
            // manually deactivating active thumbnail since swiper always keeps one slide as active
            this.swiperInstance.slides[this.swiperInstance.activeIndex].classList.remove(this.activeClassName);
        },
        closeImage() {
            this.showImage = false;
        },
        closeOnEscape(swiper, keyCode) {
            if (keyCode === 27) {
                this.closeImage();
            }
        },
        activeImageLoaded() {
            this.activeImageLoading = false;
        }
    },

    watch: {
        'activeImage.large'() {
            this.activeImageLoading = true;
        },
        activeImage(activeImage, oldActiveImage) {
            const isInitialImageDisplay = activeImage && !oldActiveImage;

            if (isInitialImageDisplay) {
                this.$nextTick(() => {
                    this.popperInstance = createPopper(this.$refs.thumbnails.$el, this.$refs.activeImage, {
                        placement: 'top-start',
                        modifiers: [
                            {
                                name: 'offset',
                                options: {
                                    offset: [0, 5]
                                }
                            },
                            {
                                name: 'flip',
                                options: {
                                    fallbackPlacements: ['bottom'],
                                    allowedAutoPlacements: ['top', 'bottom']
                                }
                            },
                            {
                                name: 'eventListeners',
                                options: {
                                    scroll: false
                                }
                            }
                        ]
                    });
                });
            } else if (!activeImage) {
                this.popperInstance && this.popperInstance.destroy();
                this.deactivateThumbnail();
                this.fixFirstSlideChange();
            }
        }
    },

    beforeDestroy() {
        this.popperInstance && this.popperInstance.destroy();
    }
};
</script>

<style lang="scss">
.image-gallery {
    &__active-image {
        background-color: transparent;
    }
}
</style>

<style scoped lang="scss">
@use 'sass:map';
@use 'sass:math';
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints';

.image-gallery {
    $height: 50px;
    $box-shadow: 0 1px 5px 0 hsla(0, 0%, 0%, 0.3);
    $z-index: map.get(variables.$z-indices, 'bottom-float') - 1;

    margin-top: (-$height) / 2;
    position: relative;

    &--opened {
        z-index: map.get(variables.$z-indices, 'modal');
    }

    &__overlay {
        position: fixed;
        background: hsla(0, 0%, 0%, 0.5);
        width: 100%;
        height: 100%;
        top: 0;
        left: 0;
    }

    &__active {
        position: absolute;
        bottom: $height + 5px;
        z-index: $z-index;
        box-shadow: 3px 3px 5px 0 hsla(0, 0%, 0%, 0.3), -3px -3px 5px 0 hsla(0, 0%, 0%, 0.3);
        width: 100%; //image wrapper should be full size for proper button and animation positioning
        aspect-ratio: 15 / 11; //image wrapper should be full size for proper button and animation positioning

        @media screen and (max-height: 900px) and (min-width: breakpoints.breakpoint-min(lg)) {
            aspect-ratio: 15 / 10; //helps prevent overflowing top viewport border with an active image (close icon as well)
        }
    }

    &__active-close {
        position: absolute;
        top: 10px;
        right: 10px;
        z-index: 1;
        padding: 3px 5px;
        backdrop-filter: contrast(0.3);
    }

    &__active-loading {
        position: absolute;
        top: 50%;
        left: 50%;
        z-index: 1;
        transform: translate(-50%, -50%);

        &-icon {
            font-size: 3em;
            color: hsla(0, 0%, 0%, 0.5);
        }
    }

    $button-width: 40px;
    &__swiper {
        height: $height;
        box-shadow: $box-shadow;
        padding-left: $button-width;
        padding-right: $button-width;
        z-index: $z-index;

        &--intact {
            .image-gallery__thumbnail--active:not(:hover) {
                opacity: 0.3;
            }
        }

        // fix for thumbnails height before swiper stylesheets load
        ::v-deep .swiper-wrapper {
            height: 100%;
        }
    }

    &__thumbnail {
        aspect-ratio: 15 / 11;
        width: auto;
        max-width: 70px;
        opacity: 0.3;
        cursor: pointer;
        transition: opacity 0.1s ease-in-out;

        &--active {
            opacity: 1;
        }

        @media (hover: hover) {
            &:hover {
                opacity: 1;
            }
        }
    }

    &__thumbnail-image {
        height: 100%;
        object-fit: cover;
    }

    &__prev-button,
    &__next-button {
        position: absolute;
        top: 0;
        height: 100%;
        width: $button-width;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 0;
        font-size: 2em;
        z-index: 1;
    }

    &__prev-button {
        left: 0;
    }

    &__next-button {
        right: 0;
    }

    &__prev-icon {
        transform: rotate(180deg);
    }

    &__prev-button,
    &__next-button {
        background-color: var(--product-select-header-bg);
    }

    &__prev-button,
    &__next-button {
        &:hover {
            color: var(--primary);
        }
    }

    &__prev-button,
    &__next-button {
        color: var(--white);
    }

    &__swiper {
        background: var(--white);
    }

    &__active-close {
        color: var(--white);
    }
}
</style>
